import React, { useState } from 'react'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { X, Upload } from 'lucide-react'
import { componentScope } from '../../lib/cssScope'
import { Button } from '../ui/Button'

interface UploadVersionModalProps {
  open: boolean
  onClose: () => void
  policyId: string
  policyTitle: string
  onSuccess: () => void
}

const UploadVersionModal: React.FC<UploadVersionModalProps> = ({
  open,
  onClose,
  policyId,
  policyTitle,
  onSuccess
}) => {
  const { t } = useI18n()
  const [loading, setLoading] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [version, setVersion] = useState('')
  const [changeLog, setChangeLog] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (!open) return null

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
      setError(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) {
      setError(t('governance.policies.upload.fileRequired', 'File is required'))
      return
    }

    try {
      setLoading(true)
      setError(null)

      const token = localStorage.getItem('token') || ''
      const formData = new FormData()
      formData.append('file', file)
      if (version) formData.append('version', version)
      if (changeLog) formData.append('changeLog', changeLog)

      const res = await fetch(API_ENDPOINTS.GOVERNANCE_POLICY_VERSION_UPLOAD(policyId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || t('governance.policies.upload.error', 'Failed to upload version'))
      }

      // Reset form
      setFile(null)
      setVersion('')
      setChangeLog('')
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.message || t('governance.policies.upload.error', 'Failed to upload version'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`${componentScope('UploadVersionModal')} fixed inset-0 z-50 flex items-center justify-center`}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative w-full max-w-lg bg-white dark:bg-neutral-900 rounded-xl shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {t('governance.policies.upload.title', 'Upload New Version')}
          </h3>
          <Button
            type="button"
            color="outline"
            iconOnly
            onClick={onClose}
            disabled={loading}
            className="border-0 bg-transparent p-1 shadow-none hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label={t('common.close', 'Close')}
            icon={<X className="w-5 h-5" />}
          >
            <span className="absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0,0,0,0)]">
              {t('common.close', 'Close')}
            </span>
          </Button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('governance.policies.upload.policy', 'Policy')}: <strong>{policyTitle}</strong>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('governance.policies.upload.file', 'File')} <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              required
              accept=".pdf,.docx,.doc,.txt"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('governance.policies.upload.fileTypes', 'Supported: PDF, DOCX, DOC, TXT (max 50MB)')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {t('governance.policies.upload.version', 'Version')}
            </label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="v1.1 (auto if empty)"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('governance.policies.upload.versionHint', 'Leave empty to auto-increment version number')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {t('governance.policies.upload.changeLog', 'Change Log')}
            </label>
            <textarea
              value={changeLog}
              onChange={(e) => setChangeLog(e.target.value)}
              rows={3}
              placeholder={t('governance.policies.upload.changeLogPlaceholder', 'Describe changes in this version...')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              color="outline"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm disabled:opacity-50"
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={loading || !file}
              className="flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50"
              icon={<Upload className="h-4 w-4" />}
            >
              {loading ? t('common.uploading', 'Uploading...') : t('governance.policies.upload.upload', 'Upload Version')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UploadVersionModal


